/**
 * Timed stepping for editor slider previews.
 *
 * Neither Embla nor the Interactivity API runs in the canvas, so a slider that
 * moves on the frontend sits perfectly still while it is being configured — and
 * pacing is the one thing a still frame cannot show. The blocks each draw their
 * own preview (a scrolling strip for brandy/slider, a panel composition for
 * brandy/showcase-slider); what they share is only the clock, which is what
 * lives here.
 *
 * Index, not pixels: a caller applies the index however its own markup moves —
 * `scrollTo` for a strip, a transform for a track — so this hook never touches
 * the DOM and cannot disagree with the layout it is stepping through.
 *
 * Playing is controlled by the caller rather than owned here. Both blocks decide
 * whether to run from the same two-part rule the Scrolling (marquee) block uses
 * — an explicit toolbar press, falling back to "is nobody editing this?" — and
 * that rule needs block state (selection, autoplay attributes) this hook has no
 * business knowing about.
 *
 * @package BrandyBlocks
 */

import { useCallback, useEffect, useRef, useState } from '@wordpress/element';

// Below this the strip never settles between steps and the preview reads as a
// stutter rather than as a slideshow. The frontend clamps its own delay to the
// same floor in Slider/render.php.
const MIN_DELAY = 500;

/**
 * Step through a slide count on a timer.
 *
 * @param {Object}   options
 * @param {number}   options.count      Positions to step through.
 * @param {number}   options.delay      Milliseconds between steps.
 * @param {boolean}  options.playing    Whether the clock is running.
 * @param {boolean}  [options.loop]     Whether the last position wraps to the
 *                                      first. When false, stepping halts at the
 *                                      end and `onEnd` fires.
 * @param {boolean}  [options.stepping] Whether playing advances the index at
 *                                      all. The Slider's auto-scroll mode
 *                                      drifts continuously instead of stepping,
 *                                      so it shares the play state but drives
 *                                      the strip itself; false leaves the index
 *                                      alone.
 * @param {Function} [options.onEnd]    Called when a non-looping run reaches the
 *                                      last position, so the caller can release
 *                                      whatever was holding it open.
 * @return {{index: number, goTo: Function}} Current position and a way to move it.
 */
export default function useStepPlayback({
	count,
	delay,
	playing,
	loop = true,
	stepping = true,
	onEnd,
}) {
	const [index, setIndex] = useState(0);

	const steps = Math.max(1, count);

	// Read through refs so the interval below can stay pinned to `delay` alone.
	// Rebuilding it whenever the slide count changed would restart the clock
	// mid-cycle every time an author added a slide.
	const stepsRef = useRef(steps);
	stepsRef.current = steps;
	const loopRef = useRef(loop);
	loopRef.current = loop;
	const onEndRef = useRef(onEnd);
	onEndRef.current = onEnd;

	const goTo = useCallback((next) => {
		const total = stepsRef.current;

		setIndex(
			loopRef.current
				? ((next % total) + total) % total
				: Math.min(Math.max(next, 0), total - 1)
		);
	}, []);

	useEffect(() => {
		if (!playing || !stepping) {
			return undefined;
		}

		const timer = setInterval(() => {
			setIndex((current) => {
				const total = stepsRef.current;
				const atEnd = current >= total - 1;

				if (atEnd && !loopRef.current) {
					onEndRef.current?.();
					return current;
				}

				return atEnd ? 0 : current + 1;
			});
		}, Math.max(MIN_DELAY, delay));

		return () => clearInterval(timer);
	}, [playing, delay, stepping]);

	// Deleting slides can leave the index parked past the end, which would show
	// an empty frame and a counter reading "4 of 3".
	useEffect(() => {
		setIndex((current) => (current > steps - 1 ? steps - 1 : current));
	}, [steps]);

	return { index, goTo };
}
